import { useEffect, useState } from "react";
import { SkyText } from "@skypointcloud/skypoint-ui";

/**
 * SD3 (#1465): what skyData says while a Validate, Connect or Save with a check waits for a slow
 * source. It is the same single request: this only counts the seconds since it started, and says
 * what is happening. No polling, no extra call. The counter is elapsed time (m:ss), not a clock time.
 */

/*
 * #2439: the hub gives up on a server it cannot reach (its connect budget, about 30 s) long before
 * any two minutes, and nothing is read until it connects. So the lines say what happens in order,
 * connecting and then reading the list, and promise no time the hub would not wait.
 */
export const WAIT_READING =
  "Connecting to the source. Once it answers, skyData reads its list of tables.";
export const WAIT_STILL_READING =
  "Still connecting to the source or reading its list of tables. Keep this page open.";
export const WAIT_STILL_WORKING =
  "Still working. A source with thousands of tables takes longer to list. Keep this page open.";
export const WAIT_TAKING_LONGER =
  "Taking longer than expected. skyData tells you what happened as soon as it knows.";
export const WAIT_SAVING = "Saving the connection and checking it once more. Keep this page open.";

const COUNTER_FROM_SECONDS = 10;
const STILL_WORKING_FROM_SECONDS = 60;
const TAKING_LONGER_FROM_SECONDS = 120;
const SAVE_LINE_FROM_SECONDS = 5;

/** Elapsed seconds, m:ss. */
export function elapsedLabel(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}

/** The sentence for a wait that has run `seconds`, or null while there is nothing to say yet. */
export function waitSentence(mode: "validate" | "save", seconds: number): string | null {
  if (mode === "save") return seconds >= SAVE_LINE_FROM_SECONDS ? WAIT_SAVING : null;
  if (seconds >= TAKING_LONGER_FROM_SECONDS) return WAIT_TAKING_LONGER;
  if (seconds >= STILL_WORKING_FROM_SECONDS) return WAIT_STILL_WORKING;
  if (seconds >= COUNTER_FROM_SECONDS) return WAIT_STILL_READING;
  return WAIT_READING;
}

function useElapsedSeconds(active: boolean): number {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    if (!active) return;
    const startedAt = Date.now();
    const timer = setInterval(() => setSeconds(Math.floor((Date.now() - startedAt) / 1000)), 1000);
    return () => {
      clearInterval(timer);
      setSeconds(0);
    };
  }, [active]);
  return active ? seconds : 0;
}

export interface ValidationWaitProps {
  /** True while the request is pending. */
  active: boolean;
  /** `validate` for Validate and Connect, `save` for a Save that also checks the connection. */
  mode: "validate" | "save";
  testId?: string;
}

export function ValidationWait({ active, mode, testId = "validation-wait" }: ValidationWaitProps) {
  const seconds = useElapsedSeconds(active);
  const sentence = active ? waitSentence(mode, seconds) : null;
  return (
    // Always mounted, so the live region exists before the line it announces.
    <div role="status" aria-live="polite" data-testid={testId} className="max-w-md">
      {sentence ? (
        <SkyText size={200} color="muted">
          {sentence}
          {seconds >= COUNTER_FROM_SECONDS ? (
            <span
              aria-hidden="true"
              data-testid={`${testId}-counter`}
            >{` Waiting ${elapsedLabel(seconds)}.`}</span>
          ) : null}
        </SkyText>
      ) : null}
    </div>
  );
}

/** The selected tables a pass found gone from the source and removed (SD2 `removedSelected`). */
export function removedSelectedSentence(
  removed: { count: number; names: string[] },
  entityLabelsLower: { singular: string; plural: string } = {
    singular: "table",
    plural: "tables",
  },
): string | null {
  if (removed.count <= 0) return null;
  const more =
    removed.names.length > 0 && removed.count > removed.names.length
      ? removed.count - removed.names.length
      : 0;
  const names = removed.names.join(", ") + (more > 0 ? ` and ${more} more` : "");
  const lead = names ? `: ${names}` : "";
  const { singular, plural } = entityLabelsLower;
  return removed.count === 1
    ? `1 selected ${singular} is no longer in the source and was removed from this connection${lead}. A renamed ${singular} shows as a new ${singular}. Tick it to keep importing it.`
    : `${removed.count} selected ${plural} are no longer in the source and were removed from this connection${lead}. A renamed ${singular} shows as a new ${singular}. Tick them to keep importing them.`;
}
